import {
  BarChart, Bar, LineChart, Line,
  XAxis, YAxis, ResponsiveContainer, Tooltip, LabelList,
} from 'recharts';
import { KpiCard, ChartMonth } from './mockData';

interface Props {
  kpi: KpiCard;
  data: ChartMonth[];
  onDrill: (to?: string) => void;
  fmt?: (n: number) => string;
  variant?: 'line' | 'bar';
  // When true: target=green, actual=gray, raw number above both bars in
  // matching color, % white between bars and month label, current-month %
  // pro-rated to today's day in month. Also renders kpi.tooltipDefinition
  // in the tooltip. Per Ohad 2026-06-07 — scoped to the patient panels.
  showAchievementPct?: boolean;
  // Force a Y-axis domain — useful when actual values cluster in a narrow
  // range (e.g. satisfaction 8.5-9 on a 0-10 scale) and auto-domain makes the
  // trend look flat. Per Ohad 2026-06-07.
  yDomain?: [number, number];
  // Hide the target series — used on satisfaction where target is fixed and
  // would overlap the actual line, making it look "straight". Per Ohad 2026-06-07.
  hideTarget?: boolean;
  // Emphasize the current (last) month — wider bars; past months condensed.
  emphasizeCurrent?: boolean;
}

const COLOR_GRAY = '#94A3B8';
const COLOR_GREEN = '#10B981';
const COLOR_BLUE = '#2563EB';
const COLOR_PCT = '#ffffff';
/* תווית ציר היא טקסט, לא גרפיקה: COLOR_GRAY נשאר למילוי הברים ולא נוגעים בו. */
const COLOR_AXIS = '#64748B';
/* 🔴 ואחוז העמידה ביעד צויר ב-COLOR_PCT הלבן גם על הציר — לבן על קלף לבן,
   כלומר בכלל לא נראה. לבן נכון רק בתוך הטולטיפ הכהה (שורה 119). */
const COLOR_PCT_AXIS = '#101D35';

function monthFractionToday(): number {
  const today = new Date();
  const day = today.getDate();
  const daysInMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
  return day / daysInMonth;
}

// Custom bar: condense past months (0.5×), widen the current month (1.1×).
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function emphBar(props: any) {
  const { x, y, width, height, fill, payload } = props;
  const f = payload?._isCurrent ? 1.1 : 0.9;
  const w = Math.max(2, width * f);
  return <rect x={x + (width - w) / 2} y={y} width={w} height={Math.max(0, height || 0)} rx={3} fill={fill} />;
}

export default function KpiChartTile({
  kpi, data, onDrill, fmt, variant = 'line', showAchievementPct = false,
  yDomain, hideTarget = false, emphasizeCurrent = false,
}: Props) {
  const clickable = !!kpi.drillTo;
  const handleClick = () => { if (clickable) onDrill(kpi.drillTo); };
  const formatter = fmt ?? ((n: number) => n.toLocaleString());

  // Color roles flip in achievement mode: target=green, actual=gray.
  const fillTarget   = showAchievementPct ? COLOR_GREEN : COLOR_GRAY;
  const fillActual   = showAchievementPct ? COLOR_GRAY  : COLOR_GREEN;
  const fillForecast = COLOR_BLUE;

  const fraction = monthFractionToday();
  const enriched = data.map((m, i) => {
    const isCurrent = i === data.length - 1;
    const observed = m.actual ?? m.forecast ?? null;
    const denomProRated = isCurrent ? m.target * fraction : m.target;
    const pctProRated = observed != null && denomProRated > 0
      ? Math.round((observed / denomProRated) * 100) : null;
    const pctAbsolute = observed != null && m.target > 0
      ? Math.round((observed / m.target) * 100) : null;
    return {
      ...m,
      _pctProRated: pctProRated,
      _pctAbsolute: pctAbsolute,
      _isCurrent: isCurrent,
      _observed: observed,
    };
  });

  // Headline: absolute number first, then month · % of target.
  // Per Ohad 2026-06-02 percentages alone aren't informative.
  const latest = enriched.slice().reverse().find((m) => m._observed != null);
  let headlineValue = '';
  let headlineMeta = '';
  if (latest && latest.target > 0 && latest._observed != null) {
    const isForecast = latest.actual == null && latest.forecast != null;
    headlineValue = formatter(latest._observed);
    const useProRated = showAchievementPct && latest._isCurrent;
    const pct = useProRated ? latest._pctProRated : latest._pctAbsolute;
    const suffix = useProRated ? '% מהיעד היחסי' : '% מהיעד';
    headlineMeta = `${latest.label} · ${pct ?? 0}${suffix}${isForecast ? ' (תחזית)' : ''}`;
  }

  const tooltipContent = ({ active, payload, label }: any) => {
    if (!active || !payload?.length) return null;
    const entry = enriched.find((m) => m.label === label);
    return (
      <div
        style={{
          background: '#101D35',
          border: '1px solid #243457',
          borderRadius: 6,
          fontSize: 12,
          color: '#101D35',
          direction: 'rtl',
          padding: '8px 10px',
          lineHeight: 1.5,
          maxWidth: 280,
        }}
      >
        <div style={{ fontWeight: 600, marginBottom: 2 }}>{label}</div>
        {payload.map((p: any) => (
          <div key={p.dataKey} style={{ color: p.color }}>
            {p.name}: {formatter(p.value)}
          </div>
        ))}
        {showAchievementPct && entry && (
          <div style={{ color: COLOR_PCT, marginTop: 2 }}>
            עמידה ביעד: {entry._isCurrent ? entry._pctProRated : entry._pctAbsolute}%
            {entry._isCurrent ? ' (יחסי ליום בחודש)' : ''}
          </div>
        )}
        {showAchievementPct && kpi.tooltipDefinition && (
          <div
            style={{
              marginTop: 6,
              paddingTop: 6,
              borderTop: '1px solid #E2E8F0',
              fontSize: 11,
              color: '#475569',
              whiteSpace: 'pre-line',
            }}
          >
            {kpi.tooltipDefinition}
          </div>
        )}
      </div>
    );
  };

  const renderRawLabel = (color: string) => (props: any) => {
    const { x, y, width, value } = props;
    if (value == null) return null;
    // Bar: width defined → center text on bar. Line: width undefined → text at point x.
    const cx = x + (width != null ? width / 2 : 0);
    return (
      <text
        x={cx}
        y={y - 4}
        textAnchor="middle"
        fill={color}
        fontSize={10}
        fontWeight={700}
      >
        {formatter(value)}
      </text>
    );
  };

  // Month tick — pushed below the white % line.
  // Single-line tick: "ינו' 93%" — month name in gray, achievement % in white.
  // Per Ohad 2026-06-07. The tick x is the band center (between target and
  // actual bars), so the combined text is centered under the correct pair.
  const renderMonthTick = (props: any) => {
    const { x, y, payload } = props;
    const m = enriched.find((e) => e.label === payload.value);
    const pct = m ? (m._isCurrent ? m._pctProRated : m._pctAbsolute) : null;
    return (
      <text
        x={x}
        y={y + 6}
        textAnchor="middle"
        dominantBaseline="hanging"
        fontSize={11}
      >
        <tspan fill={COLOR_AXIS}>{payload.value}</tspan>
        {pct != null && (
          <tspan fill={COLOR_PCT_AXIS} fontWeight={800}>{` ${pct}%`}</tspan>
        )}
      </text>
    );
  };

  const chartMargin = showAchievementPct
    ? { top: 18, right: 2, left: 2, bottom: 0 }
    : { top: 18, right: 0, left: 0, bottom: 0 };
  const lineChartMargin = showAchievementPct
    ? { top: 14, right: 12, left: 12, bottom: 0 }
    : { top: 8, right: 8, left: 8, bottom: 0 };

  return (
    <div
      className={`exec-kpi exec-kpi-chart topic-${kpi.topic}${clickable ? ' clickable' : ''}`}
      onClick={handleClick}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={clickable ? (e) => { if (e.key === 'Enter') handleClick(); } : undefined}
    >
      <div className="exec-kpi-chart-body">
        <div className="exec-kpi-chart-head">
          <span className="exec-kpi-label">{kpi.label}</span>
          {headlineValue && (
            <span className="exec-kpi-chart-headline">
              <span className="exec-kpi-chart-value">{headlineValue}</span>
              <span className="exec-kpi-chart-meta">{headlineMeta}</span>
            </span>
          )}
        </div>
        {kpi.extraNote && (
          <div className="exec-kpi-chart-extra">{kpi.extraNote}</div>
        )}
        <div className="exec-kpi-chart-canvas">
          <ResponsiveContainer width="100%" height="100%">
            {variant === 'line' ? (
              <LineChart data={enriched} margin={lineChartMargin}>
                <XAxis
                  dataKey="label"
                  tick={showAchievementPct ? renderMonthTick : { fontSize: 11, fill: COLOR_AXIS }}
                  axisLine={false}
                  tickLine={false}
                  interval={0}
                  height={showAchievementPct ? 26 : 24}
                />
                <YAxis hide domain={yDomain ?? ['auto', 'auto']} />
                <Tooltip
                  cursor={{ stroke: 'rgba(148, 163, 184, 0.25)', strokeWidth: 1 }}
                  content={tooltipContent}
                />
                {!hideTarget && (
                  <Line
                    type="monotone" dataKey="target" name="יעד"
                    stroke={fillTarget} strokeWidth={2} strokeDasharray="4 4"
                    dot={{ r: 2 }} activeDot={{ r: 4 }}
                    isAnimationActive={false}
                  />
                )}
                <Line
                  type="monotone" dataKey="actual" name="בפועל"
                  stroke={fillActual} strokeWidth={2.5}
                  dot={{ r: 3 }} activeDot={{ r: 5 }}
                  isAnimationActive={false}
                  connectNulls={false}
                >
                  {!hideTarget && (
                    <LabelList dataKey="actual" content={renderRawLabel(fillActual)} />
                  )}
                </Line>
                <Line
                  type="monotone" dataKey="forecast" name="תחזית"
                  stroke={fillForecast} strokeWidth={2.5} strokeDasharray="2 4"
                  dot={{ r: 3 }} activeDot={{ r: 5 }}
                  isAnimationActive={false}
                  connectNulls={false}
                >
                  {!hideTarget && (
                    <LabelList dataKey="forecast" content={renderRawLabel(fillForecast)} />
                  )}
                </Line>
              </LineChart>
            ) : (
              <BarChart
                data={enriched}
                barGap={showAchievementPct ? 10 : 2}
                barCategoryGap={showAchievementPct ? '14%' : '8.5%'}
                margin={chartMargin}
              >
                <XAxis
                  dataKey="label"
                  tick={showAchievementPct ? renderMonthTick : { fontSize: 11, fill: COLOR_AXIS }}
                  axisLine={false}
                  tickLine={false}
                  interval={0}
                  height={showAchievementPct ? 26 : 24}
                />
                <Tooltip
                  cursor={{ fill: 'rgba(148, 163, 184, 0.08)' }}
                  content={tooltipContent}
                />
                <Bar
                  dataKey="target" name="יעד" fill={fillTarget}
                  radius={[3, 3, 0, 0]} isAnimationActive={false}
                  shape={emphasizeCurrent ? emphBar : undefined}
                >
                  <LabelList dataKey="target" content={renderRawLabel(fillTarget)} />
                </Bar>
                <Bar
                  dataKey="actual" name="בפועל" fill={fillActual}
                  radius={[3, 3, 0, 0]} isAnimationActive={false}
                  shape={emphasizeCurrent ? emphBar : undefined}
                >
                  <LabelList dataKey="actual" content={renderRawLabel(fillActual)} />
                </Bar>
                {!showAchievementPct && (
                  <Bar
                    dataKey="forecast" name="תחזית" fill={fillForecast}
                    radius={[3, 3, 0, 0]} isAnimationActive={false}
                    shape={emphasizeCurrent ? emphBar : undefined}
                  >
                    <LabelList dataKey="forecast" content={renderRawLabel(fillForecast)} />
                  </Bar>
                )}
              </BarChart>
            )}
          </ResponsiveContainer>
        </div>
        <div className="exec-kpi-chart-legend">
          {!hideTarget && <LegendDot color={fillTarget} label="יעד" />}
          <LegendDot color={fillActual} label="בפועל" />
          {!showAchievementPct && <LegendDot color={fillForecast} label="תחזית" />}
        </div>
      </div>
    </div>
  );
}

function LegendDot({ color, label }: { color: string; label: string }) {
  return (
    <span className="exec-kpi-chart-legend-item">
      <span className="exec-kpi-chart-legend-dot" style={{ background: color }} />
      {label}
    </span>
  );
}
